import i18next from 'i18next';

type McpToolTranslationOptions = {
  reasoning?: string;
  output?: string;
  noop?: string;
  error?: string;
};

/**
 * Builds ai-tool namespace keys for an MCP tool (`serverName__toolName`).
 */
function mcpToolTranslations(
  serverName: string,
  toolName: string,
  label: string,
  options?: McpToolTranslationOptions,
): Record<string, string> {
  const key = `${serverName}__${toolName}`;
  return {
    [key]: label,
    [`${key}Reasoning`]: options?.reasoning ?? `**${label}**`,
    [`${key}Output`]: options?.output ?? `**${label}** completed`,
    [`${key}Noop`]: options?.noop ?? `**${label}** returned no results`,
    [`${key}Error`]: options?.error ?? `**Error:** ${label}`,
  };
}

/** Registers English ai-tool translations for known MCP servers and generic MCP fallbacks. */
export default function initI18n(): void {
  i18next.addResourceBundle('en', 'ai-tool', {
    _mcpReasoning: 'Running **{{shortName}}** on `{{serverName}}`',
    _mcpOutput: '**{{shortName}}** completed on `{{serverName}}`',
    _mcpNoop: '**{{shortName}}** returned no results on `{{serverName}}`',
    _mcpError: '**Error** running {{shortName}} on `{{serverName}}`',

    ...mcpToolTranslations('forge-bash', 'run', 'Bash command', {
      reasoning: '**Bash run** `{{bashCommandSummary}}`',
      output: '**Bash ran** `{{bashCommandSummary}}`',
    }),

    ...mcpToolTranslations('forge-discovery', 'list_departments', 'List departments', {
      reasoning: '**Listing** departments',
      output: '**Listed {{count}} departments**',
      noop: '**No departments** found',
    }),
    ...mcpToolTranslations('forge-discovery', 'list_dept_projects', 'List dept projects', {
      reasoning: '**Listing projects** in `{{toolCall.input.department}}`',
      output: '**Listed {{count}} projects** in `{{toolCall.input.department}}`',
      noop: '**No projects** in `{{toolCall.input.department}}`',
    }),
    ...mcpToolTranslations('forge-discovery', 'list_projects', 'List projects', {
      reasoning: '**Listing projects** across departments',
      output: '**Listed {{count}} projects** across {{deptCount}} departments',
      noop: '**No projects** found across departments',
    }),
    ...mcpToolTranslations('forge-discovery', 'get_project', 'Get project', {
      reasoning:
        '**Getting project** `{{toolCall.input.project}}` in `{{toolCall.input.department}}`',
      output:
        '**Got project `{{toolCall.input.project}}`** — phase {{projectPhase}}, {{fileCount}} files',
      noop: '**Project `{{toolCall.input.project}}`** not found in `{{toolCall.input.department}}`',
    }),
    ...mcpToolTranslations('forge-discovery', 'list_skills', 'List skills', {
      reasoning: '**Listing skills** {{skillsScope}}',
      output: '**Listed {{count}} skills** {{skillsScope}}',
      noop: '**No skills** found {{skillsScope}}',
    }),
    ...mcpToolTranslations('forge-discovery', 'find', 'Find in discovery', {
      reasoning: '**Searching discovery** for `{{toolCall.input.query}}`',
      output: '**Found {{count}} matches** for `{{toolCall.input.query}}`{{topMatchSummary}}',
      noop: '**No matches** for `{{toolCall.input.query}}`',
    }),
    ...mcpToolTranslations('forge-discovery', 'get_skill', 'Get skill', {
      reasoning: '**Getting skill** `{{toolCall.input.skill}}`{{skillScope}}',
      output: '**Loaded skill** `{{toolCall.input.skill}}`',
      noop: '**Skill `{{toolCall.input.skill}}`** not found{{skillScope}}',
    }),

    ...mcpToolTranslations('forge-typesense', 'search_knowledge', 'Search knowledge', {
      reasoning: '**Searching knowledge** for `{{toolCall.input.query}}`',
      output: '**Found {{count}}** matches ({{totalFound}} total) for `{{toolCall.input.query}}`',
      noop: '**No** knowledge matches for `{{toolCall.input.query}}`',
    }),
    ...mcpToolTranslations('forge-typesense', 'get_file_summary', 'Get file summary', {
      reasoning: '**Summarizing** `{{toolCall.input.path}}`',
      output: '**Got summary** for `{{toolCall.input.path}}`',
    }),

    ...mcpToolTranslations('forge-filesystem', 'read_text_file', 'Read file', {
      reasoning: '**Reading** `{{displayPath}}`',
      output: '**Read** `{{displayPath}}`',
    }),
    ...mcpToolTranslations('forge-filesystem', 'write_file', 'Write file', {
      reasoning: '**Writing** `{{displayPath}}`',
      output: '**Wrote** `{{displayPath}}`',
    }),
    ...mcpToolTranslations('forge-filesystem', 'edit_file', 'Edit file', {
      reasoning: '**Editing** `{{displayPath}}`',
      output: '**Edited** `{{displayPath}}`',
    }),
    ...mcpToolTranslations('forge-filesystem', 'list_directory', 'List directory', {
      reasoning: '**Listing** `{{displayPath}}`',
      output: '**Listed {{count}}** entries in `{{displayPath}}`',
      noop: '**No entries** in `{{displayPath}}`',
      error: '**Error listing** `{{displayPath}}` — {{errorMessage}}',
    }),
    ...mcpToolTranslations('forge-filesystem', 'search_files', 'Search files', {
      reasoning: '**Searching files** in `{{displayPath}}` for `{{toolCall.input.pattern}}`',
      output:
        '**Found {{count}}** files in `{{displayPath}}` matching `{{toolCall.input.pattern}}`',
      noop: '**No** files in `{{displayPath}}` matching `{{toolCall.input.pattern}}`',
    }),
    ...mcpToolTranslations('forge-filesystem', 'trash_file', 'Trash file', {
      reasoning: '**Trashing** `{{displayPath}}`',
      output: '**Trashed** `{{displayPath}}`',
    }),
    ...mcpToolTranslations('forge-filesystem', 'trash_directory', 'Trash directory', {
      reasoning: '**Trashing directory** `{{displayPath}}`',
      output: '**Trashed directory** `{{displayPath}}`',
    }),
    ...mcpToolTranslations('forge-filesystem', 'untrash_file', 'Untrash file', {
      reasoning: '**Restoring** `{{displayTrashPath}}`',
      output: '**Restored** `{{displayTrashPath}}`',
    }),
    ...mcpToolTranslations('forge-filesystem', 'untrash_directory', 'Untrash directory', {
      reasoning: '**Restoring directory** `{{displayTrashPath}}`',
      output: '**Restored directory** `{{displayTrashPath}}`',
    }),
    ...mcpToolTranslations('forge-filesystem', 'list_trash', 'List trash', {
      reasoning: '**Listing** trash',
      output: '**Listed {{count}}** trashed items',
      noop: '**Trash** is empty',
    }),
    ...mcpToolTranslations('forge-filesystem', 'empty_trash', 'Empty trash', {
      reasoning: '**Emptying** trash',
      output: '**Emptied** trash',
    }),
  });
}
